import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import { FormattedMessage, useIntl } from 'react-intl'
import dynamic from 'next/dynamic'

import { getEmail } from 'src/comps/Voucher/selectors'
import { getStatus } from 'client/contexts/CancelBooking/selectors'
import { getStatus as getEmailStatus } from 'src/effects/VerifyEmail/selectors'
import { isCancellable } from 'client/utils'

import { STATUS_REJECTED, STATUS_OK } from 'client/contexts/CancelBooking/constants'
import { VARIANT_ALONE, VARIANT_DEFAULT } from 'client/components/Modal/constants'

import ButtonSimple from 'src/comps/ButtonSimple'
import CancellationMessage from 'src/comps/CancellationMessage'
import IconCheckmark from 'client/icons/Checkmark'
import ListItem from 'src/comps/ListItem'
import Modal from 'client/components/Modal'
import VerifyEmailModal from 'src/effects/VerifyEmail'

import { CancellationPoliciesBase, Title } from './styles'
import messages from './messages'
import { useMarket } from 'src/hooks/useMarket'

const CancelBooking = dynamic(() => import('src/comps/CancelBooking'), { ssr: false })
const CancelBookingSurvey = dynamic(() => import('src/comps/CancellBookingSurvey'), { ssr: false })

const modalConfig = {
  'cancelation-email': {
    titleKey: '',
    variant: VARIANT_ALONE,
    component: VerifyEmailModal,
    buttonText: 'reservation.cancel-your-booking',
  },
  'cancelation-survey': {
    titleKey: 'reservation.cancel-your-booking',
    variant: VARIANT_DEFAULT,
    component: CancelBookingSurvey,
    buttonText: '',
  },
  'cancel-booking': {
    titleKey: 'reservation.cancel-your-booking',
    variant: VARIANT_DEFAULT,
    component: CancelBooking,
    buttonText: '',
  },
}

export const getIsMarketWithCancelationSurvey = locale => {
  return locale !== 'sk-SK'
}

export const getModalVariant = (status, email, locale) => {
  const isEmailValidated = email && status === STATUS_OK

  const isMarketWithCancelationSurvey = getIsMarketWithCancelationSurvey(locale)

  if (isEmailValidated && isMarketWithCancelationSurvey) {
    return 'cancelation-survey'
  }
  if (isEmailValidated && !isMarketWithCancelationSurvey) {
    return 'cancel-booking'
  }
  return 'cancelation-email'
}

const CancellationPolicies = ({
  cancellationPolicies,
  confirmedStatus,
  generatedWallet,
  hash,
  internal,
  pendingAmount,
  rateName,
  voucherIssues,
  idBooking,
}) => {
  const intl = useIntl()
  const statusEmail = useSelector(getEmailStatus(hash))
  const isCancelRejected = useSelector(getStatus(hash))
  const email = useSelector(getEmail)
  const [open, setOpen] = useState(false)
  const { locale } = useMarket()

  useEffect(() => {
    if (isCancelRejected === STATUS_REJECTED || isCancelRejected === STATUS_OK) {
      setOpen(false)
    }
  }, [isCancelRejected])

  const modalVariant = getModalVariant(statusEmail, email, locale)

  const { component: Component, titleKey, variant, buttonText } = modalConfig[modalVariant]

  const modalTitle = titleKey ? intl.formatMessage({ id: titleKey }) : ''

  return (
    <CancellationPoliciesBase>
      <Title>
        <FormattedMessage {...messages.cancellationPolicies} />
      </Title>

      <ListItem icon={IconCheckmark}>
        <FormattedMessage {...messages.confirming} values={{ rate_name: rateName }} />
      </ListItem>

      <ListItem>
        <CancellationMessage
          pendingAmount={pendingAmount}
          cancellationDate={cancellationPolicies[0]?.cancellationDate}
          cancellationAmount={cancellationPolicies[0]?.amount}
          currency={cancellationPolicies[0]?.currency}
        />
      </ListItem>

      {isCancellable(cancellationPolicies) && confirmedStatus && !voucherIssues && (
        <ButtonSimple
          onClick={() => setOpen(!open)}
          variant="primary"
          dataTestId="cancellation-btn"
          text={intl.formatMessage(messages.cancelYourBooking)}
        />
      )}

      <Modal
        title={modalTitle}
        handleClose={() => setOpen(false)}
        show={open}
        variant={variant}
        width="580px"
      >
        <Component
          hash={hash}
          closeModal={() => setOpen(false)}
          generatedWallet={generatedWallet}
          internal={internal}
          email={email}
          idBooking={idBooking}
          buttonText={buttonText && intl.formatMessage({ id: buttonText })}
        />
      </Modal>
    </CancellationPoliciesBase>
  )
}

CancellationPolicies.propTypes = {
  cancellationPolicies: PropTypes.arrayOf(Object),
  confirmedStatus: PropTypes.bool,
  generatedWallet: PropTypes.number,
  hash: PropTypes.string,
  internal: PropTypes.number,
  pendingAmount: PropTypes.number,
  rateName: PropTypes.string,
  voucherIssues: PropTypes.bool,
  idBooking: PropTypes.number,
}

export default CancellationPolicies
